import * as THREE from 'three';
import * as CANNON from 'cannon-es';

const GRAVITY = 26;
const THROW_SPEED = 15.5;
const FUSE_S = 2.5;
const BLAST_RADIUS_BASE = 4.5;
const MAX_DAMAGE_BASE = 88;
export const GRENADE_MAX = 3;
export const STINK_GRENADE_MAX = 2;
export const SMOKE_GRENADE_MAX = 2;
export const BOOGIE_GRENADE_MAX = 2;
export const SHOCKWAVE_GRENADE_MAX = 2;
export const CLINGER_GRENADE_MAX = 2;

const STINK_RADIUS_BASE = 4.2;
const STINK_DPS_BASE = 4;
const STINK_DURATION = 5;

const SMOKE_RADIUS = 6;
const SMOKE_HEIGHT = 4;
const SMOKE_DURATION = 8;

const BOOGIE_RADIUS = 5;
const BOOGIE_DURATION = 4;

const SHOCKWAVE_RADIUS = 8;
const SHOCKWAVE_FORCE = 22;

const CLINGER_FUSE = 2.0;
const CLINGER_RADIUS = 3;
const CLINGER_DAMAGE = 75;

type GrenadeKind = 'frag' | 'stink' | 'smoke' | 'boogie' | 'shockwave' | 'clinger';

type LiveGrenade = {
  mesh: THREE.Mesh;
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  fuse: number;
  grounded: boolean;
  kind: GrenadeKind;
  /** Clinger: once stuck, count down independently */
  stuck?: boolean;
  stuckFuse?: number;
};

type BlastFx = {
  mesh: THREE.Mesh;
  t: number;
  maxT: number;
};

type StinkCloud = {
  mesh: THREE.Mesh;
  pos: THREE.Vector3;
  life: number;
};

type SmokeCloud = {
  mesh: THREE.Mesh;
  pos: THREE.Vector3;
  life: number;
};

type CookState = {
  kind: GrenadeKind;
  cookT: number;
  ring: THREE.Mesh;
};

export type GrenadeExplosionResult = {
  x: number;
  y: number;
  z: number;
  playerDamage: number;
  botHits: { killed: boolean; name: string; damage: number; pos: THREE.Vector3 }[];
  buildsDestroyed: number;
};

export type StinkTickCallback = (
  cx: number,
  cy: number,
  cz: number,
  radius: number,
  damage: number,
) => void;

export type BoogieCallback = (pos: THREE.Vector3, radius: number, duration: number) => void;
export type ShockwaveGrenadeCallback = (pos: THREE.Vector3, radius: number, force: number) => void;
export type ClingerDamageCallback = (result: GrenadeExplosionResult) => void;

export class FpsGrenades {
  private static radiusMul = 1;
  private static damageMul = 1;

  /** LTM / modifier hook — radius and damage scale independently (defaults 1). */
  static setExplosiveMultiplier(m: { radius?: number; damage?: number } | number): void {
    if (typeof m === 'number') {
      FpsGrenades.radiusMul = m;
      FpsGrenades.damageMul = m;
      return;
    }
    if (m.radius !== undefined) FpsGrenades.radiusMul = m.radius;
    if (m.damage !== undefined) FpsGrenades.damageMul = m.damage;
  }

  private live: LiveGrenade[] = [];
  private blasts: BlastFx[] = [];
  private stinkClouds: StinkCloud[] = [];
  private smokeClouds: SmokeCloud[] = [];

  private count = GRENADE_MAX;
  private stinkCount = STINK_GRENADE_MAX;
  private smokeCount = SMOKE_GRENADE_MAX;
  private boogieCount = BOOGIE_GRENADE_MAX;
  private shockwaveCount = SHOCKWAVE_GRENADE_MAX;
  private clingerCount = CLINGER_GRENADE_MAX;

  private boogieCallback?: BoogieCallback;
  private shockwaveGrenadeCallback?: ShockwaveGrenadeCallback;
  private cooking: CookState | null = null;

  private readonly grenadeGeo = new THREE.SphereGeometry(0.11, 10, 10);
  private readonly grenadeMat = new THREE.MeshStandardMaterial({
    color: 0x3a4a32,
    roughness: 0.55,
    metalness: 0.35,
    emissive: 0x1a2218,
    emissiveIntensity: 0.25,
  });
  private readonly stinkGrenadeMat = new THREE.MeshStandardMaterial({
    color: 0x2a6a28,
    roughness: 0.5,
    metalness: 0.2,
    emissive: 0x1a4420,
    emissiveIntensity: 0.45,
  });
  private readonly smokeGrenadeMat = new THREE.MeshStandardMaterial({
    color: 0x888899,
    roughness: 0.7,
    metalness: 0.1,
    emissive: 0x444455,
    emissiveIntensity: 0.2,
  });
  private readonly boogieGrenadeMat = new THREE.MeshStandardMaterial({
    color: 0xdd44cc,
    roughness: 0.4,
    metalness: 0.3,
    emissive: 0x881188,
    emissiveIntensity: 0.55,
  });
  private readonly shockwaveGrenadeMat = new THREE.MeshStandardMaterial({
    color: 0x4488ff,
    roughness: 0.35,
    metalness: 0.45,
    emissive: 0x2244aa,
    emissiveIntensity: 0.55,
  });
  private readonly clingerGrenadeMat = new THREE.MeshStandardMaterial({
    color: 0xff6622,
    roughness: 0.5,
    metalness: 0.25,
    emissive: 0xaa2200,
    emissiveIntensity: 0.45,
  });

  constructor(
    private readonly sceneRoot: THREE.Group,
    private readonly world: CANNON.World,
  ) {}

  // ── Inventory getters ───────────────────────────────────────────────────────

  getCount(): number { return this.count; }
  getStinkCount(): number { return this.stinkCount; }
  getSmokeCount(): number { return this.smokeCount; }
  getBoogieCount(): number { return this.boogieCount; }
  getShockwaveCount(): number { return this.shockwaveCount; }
  getClingerCount(): number { return this.clingerCount; }

  // ── Callback registration ───────────────────────────────────────────────────

  setBoogieCallback(cb: BoogieCallback): void {
    this.boogieCallback = cb;
  }

  setShockwaveCallback(cb: ShockwaveGrenadeCallback): void {
    this.shockwaveGrenadeCallback = cb;
  }

  // ── Throw methods ───────────────────────────────────────────────────────────

  /** Start cooking a frag grenade (hold throw key). */
  beginCook(kind: GrenadeKind = 'frag'): boolean {
    if (this.cooking) return true;
    if (kind === 'frag' && this.count <= 0) return false;
    if (kind === 'stink' && this.stinkCount <= 0) return false;
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.35, 0.55, 36),
      new THREE.MeshBasicMaterial({
        color: 0xff8844,
        transparent: true,
        opacity: 0.55,
        side: THREE.DoubleSide,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    this.sceneRoot.add(ring);
    this.cooking = { kind, cookT: 0, ring };
    return true;
  }

  isCooking(): boolean {
    return this.cooking !== null;
  }

  getCookRatio(): number {
    if (!this.cooking) return 0;
    return Math.min(1, this.cooking.cookT / FUSE_S);
  }

  /** Update cook telegraph ring while holding throw key. Returns true if cooked too long (explode). */
  updateCook(dt: number, groundPos: THREE.Vector3): boolean {
    if (!this.cooking) return false;
    this.cooking.cookT += dt;
    const ratio = Math.min(1, this.cooking.cookT / FUSE_S);
    const blastR = FpsGrenades.getBlastRadius();
    const scale = 0.4 + ratio * blastR * 1.85;
    this.cooking.ring.position.set(groundPos.x, groundPos.y + 0.06, groundPos.z);
    this.cooking.ring.scale.setScalar(scale / 0.55);
    const mat = this.cooking.ring.material as THREE.MeshBasicMaterial;
    mat.opacity = 0.35 + ratio * 0.45;
    return this.cooking.cookT >= FUSE_S - 0.15;
  }

  /** Release cooked grenade; shorter fuse based on cook time. */
  releaseCook(origin: THREE.Vector3, direction: THREE.Vector3): boolean {
    if (!this.cooking) return false;
    const { kind, cookT, ring } = this.cooking;
    ring.removeFromParent();
    ring.geometry.dispose();
    (ring.material as THREE.Material).dispose();
    this.cooking = null;
    const remainingFuse = Math.max(0.35, FUSE_S - cookT);
    return this.spawnGrenade(origin, direction, kind, remainingFuse);
  }

  cancelCook(): void {
    if (!this.cooking) return;
    this.cooking.ring.removeFromParent();
    this.cooking.ring.geometry.dispose();
    (this.cooking.ring.material as THREE.Material).dispose();
    this.cooking = null;
  }

  /** Cooked too long — consume grenade and return kind for in-hand detonation. */
  detonateCookInHand(): GrenadeKind | null {
    if (!this.cooking) return null;
    const kind = this.cooking.kind;
    this.cancelCook();
    switch (kind) {
      case 'frag':
        if (this.count > 0) this.count--;
        break;
      case 'stink':
        if (this.stinkCount > 0) this.stinkCount--;
        break;
      default:
        break;
    }
    return kind;
  }

  /** Frag grenade — classic timed explosive. */
  tryThrow(origin: THREE.Vector3, direction: THREE.Vector3): boolean {
    return this.spawnGrenade(origin, direction, 'frag');
  }

  /** Stink grenade — green DoT cloud. */
  tryThrowStink(origin: THREE.Vector3, direction: THREE.Vector3): boolean {
    return this.spawnGrenade(origin, direction, 'stink');
  }

  /** Smoke grenade — grey vision-blocking cylinder, 8s. */
  throwSmoke(origin: THREE.Vector3, direction: THREE.Vector3): boolean {
    return this.spawnGrenade(origin, direction, 'smoke');
  }

  /** Boogie grenade — forces nearby bots into dance state for 4s. */
  throwBoogie(origin: THREE.Vector3, direction: THREE.Vector3): boolean {
    return this.spawnGrenade(origin, direction, 'boogie');
  }

  /** Shockwave grenade — radial knockback, no damage. */
  throwShockwave(origin: THREE.Vector3, direction: THREE.Vector3): boolean {
    return this.spawnGrenade(origin, direction, 'shockwave');
  }

  /** Clinger grenade — sticks to surface on contact, 2s fuse, 75 dmg r3. */
  throwClinger(origin: THREE.Vector3, direction: THREE.Vector3): boolean {
    return this.spawnGrenade(origin, direction, 'clinger');
  }

  // ── Inventory text ──────────────────────────────────────────────────────────

  getInventoryText(): string {
    return [
      `Frag x${this.count}`,
      `Stink x${this.stinkCount}`,
      `Smoke x${this.smokeCount}`,
      `Boogie x${this.boogieCount}`,
      `Shock x${this.shockwaveCount}`,
      `Clinger x${this.clingerCount}`,
    ].join('  ');
  }

  // ── Internal spawn ──────────────────────────────────────────────────────────

  private getMat(kind: GrenadeKind): THREE.Material {
    switch (kind) {
      case 'stink':     return this.stinkGrenadeMat;
      case 'smoke':     return this.smokeGrenadeMat;
      case 'boogie':    return this.boogieGrenadeMat;
      case 'shockwave': return this.shockwaveGrenadeMat;
      case 'clinger':   return this.clingerGrenadeMat;
      default:          return this.grenadeMat;
    }
  }

  private spawnGrenade(
    origin: THREE.Vector3,
    direction: THREE.Vector3,
    kind: GrenadeKind,
    fuseOverride?: number,
  ): boolean {
    switch (kind) {
      case 'frag':      if (this.count <= 0)          return false; this.count--;          break;
      case 'stink':     if (this.stinkCount <= 0)     return false; this.stinkCount--;     break;
      case 'smoke':     if (this.smokeCount <= 0)     return false; this.smokeCount--;     break;
      case 'boogie':    if (this.boogieCount <= 0)    return false; this.boogieCount--;    break;
      case 'shockwave': if (this.shockwaveCount <= 0) return false; this.shockwaveCount--; break;
      case 'clinger':   if (this.clingerCount <= 0)   return false; this.clingerCount--;   break;
    }

    const dir = direction.clone().normalize();
    const vel = dir.clone().multiplyScalar(THROW_SPEED);
    vel.y += 4.2;

    const mesh = new THREE.Mesh(this.grenadeGeo, this.getMat(kind));
    mesh.castShadow = true;
    mesh.position.copy(origin).add(dir.clone().multiplyScalar(0.45));
    this.sceneRoot.add(mesh);

    this.live.push({
      mesh,
      pos: mesh.position.clone(),
      vel,
      fuse: fuseOverride ?? FUSE_S,
      grounded: false,
      kind,
    });
    return true;
  }

  // ── Update loop ─────────────────────────────────────────────────────────────

  update(
    dt: number,
    onExplode: (result: GrenadeExplosionResult) => void,
    onStinkTick?: StinkTickCallback,
  ): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const g = this.live[i]!;

      // Clinger stuck: ignore physics, count down stuckFuse
      if (g.kind === 'clinger' && g.stuck) {
        g.stuckFuse! -= dt;
        if (g.stuckFuse! <= 0) {
          this.detonate(g, onExplode);
          this.live.splice(i, 1);
        }
        continue;
      }

      g.fuse -= dt;
      g.vel.y -= GRAVITY * dt;

      const next = g.pos.clone().add(g.vel.clone().multiplyScalar(dt));
      const from = new CANNON.Vec3(g.pos.x, g.pos.y, g.pos.z);
      const to = new CANNON.Vec3(next.x, next.y, next.z);
      const res = new CANNON.RaycastResult();
      const hit = this.world.raycastClosest(from, to, { skipBackfaces: true }, res);

      if (hit && res.hasHit && res.distance < g.pos.distanceTo(next) + 0.02) {
        const n = res.hitNormalWorld;
        g.pos.set(res.hitPointWorld.x, res.hitPointWorld.y + 0.11, res.hitPointWorld.z);

        if (g.kind === 'clinger') {
          // Stick on first surface contact
          g.stuck = true;
          g.stuckFuse = CLINGER_FUSE;
          g.mesh.position.copy(g.pos);
          continue;
        }

        const vn = g.vel.dot(new THREE.Vector3(n.x, n.y, n.z));
        if (vn < 0) {
          g.vel.x -= n.x * vn * 1.35;
          g.vel.y -= n.y * vn * 1.35;
          g.vel.z -= n.z * vn * 1.35;
        }
        g.vel.multiplyScalar(0.72);
        g.grounded = true;
        if (g.grounded && Math.hypot(g.vel.x, g.vel.z) > 0.35) {
          g.vel.x *= 0.94;
          g.vel.z *= 0.94;
        }
      } else {
        g.pos.copy(next);
      }

      g.mesh.position.copy(g.pos);
      g.mesh.rotation.x += dt * 9.5;
      g.mesh.rotation.z += dt * 7.2;

      const lowSpeed = g.grounded && Math.hypot(g.vel.x, g.vel.y, g.vel.z) < 0.55;
      if (g.fuse <= 0 || (g.grounded && lowSpeed && g.fuse < FUSE_S - 0.08)) {
        this.detonate(g, onExplode);
        this.live.splice(i, 1);
      }
    }

    // Stink clouds
    for (let i = this.stinkClouds.length - 1; i >= 0; i--) {
      const cloud = this.stinkClouds[i]!;
      cloud.life -= dt;
      const u = 1 - Math.max(0, cloud.life / STINK_DURATION);
      const pulse = 1 + Math.sin(performance.now() * 0.004 + i) * 0.06;
      const stinkR = FpsGrenades.getStinkRadius();
      cloud.mesh.scale.setScalar((stinkR * 2 * pulse * (0.82 + u * 0.18)) / 2);
      (cloud.mesh.material as THREE.MeshBasicMaterial).opacity = 0.34 * (cloud.life / STINK_DURATION);
      onStinkTick?.(cloud.pos.x, cloud.pos.y, cloud.pos.z, stinkR, FpsGrenades.getStinkDps() * dt);
      if (cloud.life <= 0) {
        cloud.mesh.removeFromParent();
        (cloud.mesh.geometry as THREE.BufferGeometry).dispose();
        (cloud.mesh.material as THREE.Material).dispose();
        this.stinkClouds.splice(i, 1);
      }
    }

    // Smoke clouds
    for (let i = this.smokeClouds.length - 1; i >= 0; i--) {
      const cloud = this.smokeClouds[i]!;
      cloud.life -= dt;
      const fadeIn = Math.min(1, (SMOKE_DURATION - cloud.life) / 0.8);
      const fadeOut = Math.min(1, cloud.life / 1.5);
      const opacity = 0.45 * Math.min(fadeIn, fadeOut);
      (cloud.mesh.material as THREE.MeshBasicMaterial).opacity = opacity;
      if (cloud.life <= 0) {
        cloud.mesh.removeFromParent();
        (cloud.mesh.geometry as THREE.BufferGeometry).dispose();
        (cloud.mesh.material as THREE.Material).dispose();
        this.smokeClouds.splice(i, 1);
      }
    }

    // Blast fx
    for (let i = this.blasts.length - 1; i >= 0; i--) {
      const b = this.blasts[i]!;
      b.t += dt;
      const u = Math.min(1, b.t / b.maxT);
      const scale = 0.35 + u * FpsGrenades.getBlastRadius() * 2.1;
      b.mesh.scale.setScalar(scale);
      (b.mesh.material as THREE.MeshBasicMaterial).opacity = 0.72 * (1 - u);
      if (b.t >= b.maxT) {
        b.mesh.removeFromParent();
        (b.mesh.geometry as THREE.BufferGeometry).dispose();
        (b.mesh.material as THREE.Material).dispose();
        this.blasts.splice(i, 1);
      }
    }
  }

  // ── Detonation ──────────────────────────────────────────────────────────────

  private detonate(g: LiveGrenade, onExplode: (result: GrenadeExplosionResult) => void): void {
    const { x, y, z } = g.pos;
    g.mesh.removeFromParent();

    switch (g.kind) {

      case 'stink': {
        const cloudGeo = new THREE.SphereGeometry(FpsGrenades.getStinkRadius(), 16, 16);
        const cloudMat = new THREE.MeshBasicMaterial({
          color: 0x44cc44,
          transparent: true,
          opacity: 0.34,
          depthWrite: false,
        });
        const cloudMesh = new THREE.Mesh(cloudGeo, cloudMat);
        cloudMesh.position.set(x, y + 0.35, z);
        this.sceneRoot.add(cloudMesh);
        this.stinkClouds.push({ mesh: cloudMesh, pos: new THREE.Vector3(x, y + 0.35, z), life: STINK_DURATION });
        return;
      }

      case 'smoke': {
        // Semi-transparent grey cylinder, 6m radius, 4m tall
        const smokeGeo = new THREE.CylinderGeometry(SMOKE_RADIUS, SMOKE_RADIUS, SMOKE_HEIGHT, 24, 1, true);
        const smokeMat = new THREE.MeshBasicMaterial({
          color: 0x999999,
          transparent: true,
          opacity: 0,          // fades in during update
          depthWrite: false,
          side: THREE.DoubleSide,
        });
        const smokeMesh = new THREE.Mesh(smokeGeo, smokeMat);
        smokeMesh.position.set(x, y + SMOKE_HEIGHT / 2, z);
        this.sceneRoot.add(smokeMesh);
        this.smokeClouds.push({ mesh: smokeMesh, pos: new THREE.Vector3(x, y + SMOKE_HEIGHT / 2, z), life: SMOKE_DURATION });
        return;
      }

      case 'boogie': {
        // Visual flash
        this.spawnBlastFx(x, y, z, 0xdd44ff, 0.3);
        const pos = new THREE.Vector3(x, y, z);
        this.boogieCallback?.(pos, BOOGIE_RADIUS, BOOGIE_DURATION);
        onExplode({ x, y, z, playerDamage: 0, botHits: [], buildsDestroyed: 0 });
        return;
      }

      case 'shockwave': {
        // Ring VFX
        const ring = new THREE.Mesh(
          new THREE.RingGeometry(0.4, 1.6, 32),
          new THREE.MeshBasicMaterial({
            color: 0x66bbff,
            transparent: true,
            opacity: 0.72,
            side: THREE.DoubleSide,
            depthWrite: false,
          }),
        );
        ring.rotation.x = -Math.PI / 2;
        ring.position.set(x, y + 0.1, z);
        this.sceneRoot.add(ring);
        this.blasts.push({ mesh: ring, t: 0, maxT: 0.45 });
        const pos = new THREE.Vector3(x, y, z);
        this.shockwaveGrenadeCallback?.(pos, SHOCKWAVE_RADIUS, SHOCKWAVE_FORCE);
        onExplode({ x, y, z, playerDamage: 0, botHits: [], buildsDestroyed: 0 });
        return;
      }

      case 'clinger': {
        this.spawnBlastFx(x, y, z, 0xff6622, 0.3);
        // Clinger damage resolved by caller via onExplode (same as frag)
        onExplode({ x, y, z, playerDamage: 0, botHits: [], buildsDestroyed: 0 });
        return;
      }

      default: { // 'frag'
        this.spawnBlastFx(x, y, z, 0xffaa44, 0.38);
        onExplode({ x, y, z, playerDamage: 0, botHits: [], buildsDestroyed: 0 });
      }
    }
  }

  private spawnBlastFx(x: number, y: number, z: number, color: number, maxT: number): void {
    const blastGeo = new THREE.SphereGeometry(1, 14, 14);
    const blastMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
    });
    const blastMesh = new THREE.Mesh(blastGeo, blastMat);
    blastMesh.position.set(x, y, z);
    this.sceneRoot.add(blastMesh);
    this.blasts.push({ mesh: blastMesh, t: 0, maxT });
  }

  // ── Static helpers ──────────────────────────────────────────────────────────

  /** Compute radial damage at explosion center (called from game after detonate). */
  static damageAtPoint(
    cx: number,
    cy: number,
    cz: number,
    targetX: number,
    targetY: number,
    targetZ: number,
  ): number {
    const dx = targetX - cx;
    const dy = targetY - cy;
    const dz = targetZ - cz;
    const d = Math.hypot(dx, dy, dz);
    const radius = FpsGrenades.getBlastRadius();
    if (d >= radius) return 0;
    const falloff = 1 - d / radius;
    return FpsGrenades.getMaxDamage() * falloff * falloff;
  }

  /** Clinger damage — fixed radius and damage, no multiplier scaling by design. */
  static clingerDamageAtPoint(
    cx: number, cy: number, cz: number,
    targetX: number, targetY: number, targetZ: number,
  ): number {
    const d = Math.hypot(targetX - cx, targetY - cy, targetZ - cz);
    if (d >= CLINGER_RADIUS) return 0;
    const falloff = 1 - d / CLINGER_RADIUS;
    return CLINGER_DAMAGE * falloff * falloff;
  }

  static getBlastRadius(): number {
    return BLAST_RADIUS_BASE * FpsGrenades.radiusMul;
  }

  static getMaxDamage(): number {
    return MAX_DAMAGE_BASE * FpsGrenades.damageMul;
  }

  static getStinkRadius(): number {
    return STINK_RADIUS_BASE * FpsGrenades.radiusMul;
  }

  static getStinkDps(): number {
    return STINK_DPS_BASE * FpsGrenades.damageMul;
  }

  static getSmokeRadius(): number { return SMOKE_RADIUS; }
  static getBoogieRadius(): number { return BOOGIE_RADIUS; }
  static getShockwaveRadius(): number { return SHOCKWAVE_RADIUS; }
  static getShockwaveForce(): number { return SHOCKWAVE_FORCE; }
  static getClingerRadius(): number { return CLINGER_RADIUS; }
  static getClingerDamage(): number { return CLINGER_DAMAGE; }

  // ── Disposal ────────────────────────────────────────────────────────────────

  dispose(): void {
    this.cancelCook();
    for (const g of this.live) g.mesh.removeFromParent();
    this.live.length = 0;
    for (const cloud of this.stinkClouds) {
      cloud.mesh.removeFromParent();
      (cloud.mesh.geometry as THREE.BufferGeometry).dispose();
      (cloud.mesh.material as THREE.Material).dispose();
    }
    this.stinkClouds.length = 0;
    for (const cloud of this.smokeClouds) {
      cloud.mesh.removeFromParent();
      (cloud.mesh.geometry as THREE.BufferGeometry).dispose();
      (cloud.mesh.material as THREE.Material).dispose();
    }
    this.smokeClouds.length = 0;
    for (const b of this.blasts) {
      b.mesh.removeFromParent();
      (b.mesh.geometry as THREE.BufferGeometry).dispose();
      (b.mesh.material as THREE.Material).dispose();
    }
    this.blasts.length = 0;
    this.grenadeGeo.dispose();
    this.grenadeMat.dispose();
    this.stinkGrenadeMat.dispose();
    this.smokeGrenadeMat.dispose();
    this.boogieGrenadeMat.dispose();
    this.shockwaveGrenadeMat.dispose();
    this.clingerGrenadeMat.dispose();
  }
}
